import { useState, useEffect, useCallback, useRef } from 'react'
import axios from 'axios'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { format, parseISO } from 'date-fns'
import { es, enUS } from 'date-fns/locale'
import { Button } from '../components/ui/button'
import { Card, CardContent } from '../components/ui/card'
import { Input } from '../components/ui/input'
import { Textarea } from '../components/ui/textarea'
import { Label } from '../components/ui/label'
import { Badge } from '../components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog'
import { toast } from 'sonner'
import { useLanguage } from '../context/LanguageContext'
import { cn } from '../lib/utils'
import { 
  ArrowLeft, Save, Trash2, CheckSquare,
  Lightbulb, Inbox, Eye, Rocket, Archive, Plus, X, Link, Loader2,
  Building2, Briefcase, User, MoreHorizontal, Circle,
  Mic, Square, Play, Pause, Volume2, Trash
} from 'lucide-react'
import { QuickAddIdeaBucketButton } from '../components/modals/IdeaBucketModal'

const API = `${process.env.REACT_APP_BACKEND_URL}/api`

// Icon map for dynamic bucket icons
const iconMap = {
  Rocket, Building2, Briefcase, User, MoreHorizontal, Circle,
  Lightbulb, Eye, Inbox, Archive
}

const STATUS_OPTIONS = [
  { value: 'inbox', icon: Inbox, label: 'inbox' },
  { value: 'review', icon: Eye, label: 'review' },
  { value: 'doing', icon: Rocket, label: 'doing' },
  { value: 'archived', icon: Archive, label: 'archived' },
]

export default function IdeaDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { t, locale } = useLanguage()
  const isNew = id === 'new'
  
  const dateLocale = locale === 'es-MX' ? es : enUS
  
  const [loading, setLoading] = useState(!isNew)
  const [saving, setSaving] = useState(false)
  const [buckets, setBuckets] = useState([])
  const [idea, setIdea] = useState({
    title: '',
    body: '',
    bucket_id: '',
    tags: [],
    status: 'inbox',
    source_url: '',
    audio_files: []
  })
  const [newTag, setNewTag] = useState('')
  
  // Audio recording states
  const [isRecording, setIsRecording] = useState(false)
  const [recordingTime, setRecordingTime] = useState(0)
  const [audioUploading, setAudioUploading] = useState(false)
  const [playingAudioId, setPlayingAudioId] = useState(null)
  const mediaRecorderRef = useRef(null)
  const audioChunksRef = useRef([])
  const recordingTimerRef = useRef(null)
  const audioPlayerRef = useRef(null)

  const MAX_RECORDING_SECONDS = 300 // 5 minutes

  // Fetch buckets first
  const fetchBuckets = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/idea-buckets`)
      setBuckets(response.data)
      // Set default bucket for new ideas
      if (isNew && response.data.length > 0) {
        const personalBucket = response.data.find(b => b.name === 'Personal')
        setIdea(prev => ({
          ...prev,
          bucket_id: personalBucket?.id || response.data[0].id
        }))
      }
    } catch (error) {
      console.error('Error fetching buckets:', error)
    }
  }, [isNew])

  const fetchIdea = useCallback(async () => {
    if (isNew) return
    
    try {
      const response = await axios.get(`${API}/crazy-ideas/${id}`)
      setIdea(response.data)
    } catch (error) {
      console.error('Error fetching idea:', error)
      toast.error(t('common.error'))
      navigate('/ideas')
    } finally {
      setLoading(false)
    }
  }, [id, isNew, navigate, t])

  useEffect(() => {
    fetchBuckets()
  }, [fetchBuckets])

  useEffect(() => {
    fetchIdea()
  }, [fetchIdea])

  // Auto-start recording if coming from new idea creation
  useEffect(() => {
    if (!isNew && !loading && searchParams.get('startRecording') === 'true') {
      // Clear the query param
      setSearchParams({}, { replace: true })
      // Start recording after a small delay to ensure component is ready
      setTimeout(() => {
        startRecording()
      }, 500)
    }
  }, [isNew, loading, searchParams, setSearchParams])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current)
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        try {
          mediaRecorderRef.current.stop()
          // Stop all tracks
          const stream = mediaRecorderRef.current.stream
          if (stream) {
            stream.getTracks().forEach(track => track.stop())
          }
        } catch (e) {
          console.error('Error stopping recording on unmount:', e)
        }
      }
    }
  }, [])

  const handleSave = async () => {
    if (!idea.body.trim()) {
      toast.error(locale === 'es-MX' ? 'El cuerpo de la idea es requerido' : 'Idea body is required')
      return
    }
    
    setSaving(true)
    try {
      if (isNew) {
        const response = await axios.post(`${API}/crazy-ideas`, {
          title: idea.title || null,
          body: idea.body,
          bucket_id: idea.bucket_id || null,
          tags: idea.tags,
          source_url: idea.source_url || null
        })
        toast.success(t('ideas.ideaSaved'))
        navigate(`/ideas/${response.data.id}`)
      } else {
        await axios.put(`${API}/crazy-ideas/${id}`, {
          title: idea.title || null,
          body: idea.body,
          bucket_id: idea.bucket_id || null,
          tags: idea.tags,
          status: idea.status,
          source_url: idea.source_url || null
        })
        toast.success(t('ideas.ideaUpdated'))
        // Navigate back to ideas list after updating
        navigate('/ideas')
      }
    } catch (error) {
      console.error('Error saving idea:', error)
      toast.error(t('ideas.errorSaving'))
    } finally {
      setSaving(false)
    }
  }

  const handleArchive = async () => {
    if (!window.confirm(locale === 'es-MX' ? '¿Archivar esta idea?' : 'Archive this idea?')) return
    
    try {
      await axios.delete(`${API}/crazy-ideas/${id}`)
      toast.success(t('ideas.ideaArchived'))
      navigate('/ideas')
    } catch (error) {
      toast.error(t('ideas.errorSaving'))
    }
  }

  const handleConvertToTask = async () => {
    if (isNew) {
      toast.error(locale === 'es-MX' ? 'Guarda la idea primero' : 'Save the idea first')
      return
    }
    
    try {
      await axios.post(`${API}/crazy-ideas/${id}/convert-to-task`)
      toast.success(t('ideas.taskCreated'))
      navigate('/tareas')
    } catch (error) {
      toast.error(t('ideas.errorSaving'))
    }
  }

  const addTag = () => {
    if (newTag.trim() && !idea.tags.includes(newTag.trim())) {
      setIdea(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }))
      setNewTag('')
    }
  }

  const removeTag = (tagToRemove) => {
    setIdea(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tagToRemove)
    }))
  }

  // Audio Recording Functions
  const startRecording = async () => {
    // If it's a new idea, save it first automatically
    if (isNew) {
      if (!idea.body || idea.body.trim() === '') {
        // Set a default body for voice note ideas
        setIdea(prev => ({ ...prev, body: locale === 'es-MX' ? '🎙️ Nota de voz' : '🎙️ Voice note' }))
      }
      
      setSaving(true)
      try {
        const response = await axios.post(`${API}/crazy-ideas`, {
          title: idea.title || null,
          body: idea.body?.trim() || (locale === 'es-MX' ? '🎙️ Nota de voz' : '🎙️ Voice note'),
          bucket_id: idea.bucket_id || null,
          tags: idea.tags,
          source_url: idea.source_url || null
        })
        
        // Navigate to the saved idea and continue with recording there
        toast.success(locale === 'es-MX' ? 'Idea guardada. Iniciando grabación...' : 'Idea saved. Starting recording...')
        navigate(`/ideas/${response.data.id}?startRecording=true`, { replace: true })
        return
      } catch (error) {
        console.error('Error saving idea:', error)
        toast.error(locale === 'es-MX' ? 'Error al guardar la idea' : 'Error saving idea')
        setSaving(false)
        return
      }
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4'
      })
      
      mediaRecorderRef.current = mediaRecorder
      audioChunksRef.current = []
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data)
        }
      }
      
      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { 
          type: mediaRecorder.mimeType 
        })
        stream.getTracks().forEach(track => track.stop())
        
        // Upload the audio
        await uploadAudio(audioBlob)
      }
      
      // Request data immediately when starting to capture from the beginning
      mediaRecorder.start(100) // Collect data every 100ms for faster response
      
      // Set states AFTER MediaRecorder is ready
      setIsRecording(true)
      setRecordingTime(0)
      
      // Start timer with small delay to ensure first chunk is captured
      setTimeout(() => {
        recordingTimerRef.current = setInterval(() => {
          setRecordingTime(prev => {
            if (prev >= MAX_RECORDING_SECONDS - 1) {
              stopRecording()
              return prev
            }
            return prev + 1
          })
        }, 1000)
      }, 100)
      
    } catch (error) {
      console.error('Error starting recording:', error)
      // Reset all states on error
      setIsRecording(false)
      setRecordingTime(0)
      setAudioUploading(false)
      toast.error(locale === 'es-MX' ? 'Error al acceder al micrófono' : 'Error accessing microphone')
    }
  }

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      // Check if recording is too short
      if (recordingTime < 1) {
        toast.error(locale === 'es-MX' ? 'Grabación muy corta. Mantén presionado al menos 1 segundo.' : 'Recording too short. Hold for at least 1 second.')
        // Cancel the recording instead of saving
        mediaRecorderRef.current.onstop = () => {
          const stream = mediaRecorderRef.current.stream
          if (stream) {
            stream.getTracks().forEach(track => track.stop())
          }
          // Reset states
          setAudioUploading(false)
        }
        mediaRecorderRef.current.stop()
        if (recordingTimerRef.current) {
          clearInterval(recordingTimerRef.current)
        }
        setIsRecording(false)
        setRecordingTime(0)
        return
      }
      mediaRecorderRef.current.stop()
    }
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current)
    }
    setIsRecording(false)
  }

  const uploadAudio = async (audioBlob) => {
    // Validate audio blob before uploading
    if (!audioBlob || audioBlob.size < 100) {
      toast.error(locale === 'es-MX' ? 'La grabación está vacía. Intenta de nuevo.' : 'Recording is empty. Please try again.')
      setAudioUploading(false)
      setRecordingTime(0)
      return
    }
    
    setAudioUploading(true)
    try {
      const formData = new FormData()
      formData.append('audio', audioBlob, 'recording.webm')
      
      const response = await axios.post(`${API}/crazy-ideas/${id}/audio`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      
      // Add the new audio to the list
      setIdea(prev => ({
        ...prev,
        audio_files: [...(prev.audio_files || []), {
          id: response.data.id,
          filename: response.data.filename,
          size_mb: response.data.size_mb,
          created_at: new Date().toISOString()
        }]
      }))
      
      toast.success(locale === 'es-MX' ? 'Audio guardado' : 'Audio saved')
    } catch (error) {
      console.error('Error uploading audio:', error)
      toast.error(locale === 'es-MX' ? 'Error al guardar el audio' : 'Error saving audio')
    } finally {
      // Always reset states to allow new recordings
      setAudioUploading(false)
      setRecordingTime(0)
      setIsRecording(false)
    }
  }

  const playAudio = async (audioId) => {
    if (playingAudioId === audioId) {
      // Pause current audio
      if (audioPlayerRef.current) {
        audioPlayerRef.current.pause()
      }
      setPlayingAudioId(null)
    } else {
      // Play new audio
      if (audioPlayerRef.current) {
        audioPlayerRef.current.pause()
        // Revoke previous blob URL to free memory
        if (audioPlayerRef.current.src?.startsWith('blob:')) {
          URL.revokeObjectURL(audioPlayerRef.current.src)
        }
      }
      
      try {
        // Get the auth token from localStorage
        const token = localStorage.getItem('token')
        if (!token) {
          toast.error(locale === 'es-MX' ? 'Sesión expirada' : 'Session expired')
          return
        }
        
        // Fetch audio with auth headers using native fetch
        const response = await fetch(`${API}/audio/${audioId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
        
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('FILE_NOT_FOUND')
          }
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        
        const blob = await response.blob()
        const objectUrl = URL.createObjectURL(blob)
        
        const audio = new Audio(objectUrl)
        audioPlayerRef.current = audio
        
        audio.onended = () => {
          setPlayingAudioId(null)
          URL.revokeObjectURL(objectUrl)
        }
        audio.onerror = () => {
          toast.error(locale === 'es-MX' ? 'Error al reproducir audio' : 'Error playing audio')
          setPlayingAudioId(null)
          URL.revokeObjectURL(objectUrl)
        }
        
        await audio.play()
        setPlayingAudioId(audioId)
      } catch (error) {
        console.error('Error loading audio:', error)
        if (error.message === 'FILE_NOT_FOUND') {
          toast.error(locale === 'es-MX' 
            ? 'Audio no disponible. El archivo puede haberse perdido al actualizar la app.' 
            : 'Audio unavailable. The file may have been lost during app update.')
        } else {
          toast.error(locale === 'es-MX' ? 'Error al cargar el audio' : 'Error loading audio')
        }
      }
    }
  }

  const deleteAudio = async (audioId) => {
    if (!window.confirm(locale === 'es-MX' ? '¿Eliminar este audio?' : 'Delete this audio?')) return
    
    try {
      await axios.delete(`${API}/crazy-ideas/${id}/audio/${audioId}`)
      setIdea(prev => ({
        ...prev,
        audio_files: (prev.audio_files || []).filter(a => a.id !== audioId)
      }))
      toast.success(locale === 'es-MX' ? 'Audio eliminado' : 'Audio deleted')
    } catch (error) {
      toast.error(locale === 'es-MX' ? 'Error al eliminar audio' : 'Error deleting audio')
    }
  }

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  if (loading) {
    return (
      <div className="p-4 md:p-8 pb-safe flex items-center justify-center min-h-[50vh]">
        <div className="animate-pulse text-muted-foreground">{t('common.loading')}</div>
      </div>
    )
  }

  return (
    <div className="p-4 md:p-8 pb-safe max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/ideas')}
          className="rounded-full"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-xl font-bold flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-amber-500" />
            {isNew ? t('ideas.newIdea') : t('ideas.title')}
          </h1>
        </div>
        {!isNew && (
          <Button
            variant="outline"
            size="icon"
            onClick={handleArchive}
            className="rounded-full text-destructive hover:text-destructive"
            data-testid="archive-btn"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Form */}
      <div className="space-y-6">
        {/* Title (optional) */}
        <div className="space-y-2">
          <Label>{t('ideas.titlePlaceholder')}</Label>
          <Input
            placeholder={t('ideas.titlePlaceholder')}
            value={idea.title || ''}
            onChange={(e) => setIdea(prev => ({ ...prev, title: e.target.value }))}
            data-testid="idea-title-input"
          />
        </div>

        {/* Body (required) */}
        <div className="space-y-2">
          <Label>{t('ideas.body')} *</Label>
          <Textarea
            placeholder={t('ideas.bodyPlaceholder')}
            value={idea.body}
            onChange={(e) => setIdea(prev => ({ ...prev, body: e.target.value }))}
            className="min-h-[150px]"
            data-testid="idea-body-input"
          />
        </div>

        {/* Audio Recording Section */}
        <div className="space-y-3">
          <Label className="flex items-center gap-2">
            <Volume2 className="w-4 h-4" />
            {locale === 'es-MX' ? 'Notas de Voz' : 'Voice Notes'}
          </Label>
          
          {/* Recording Controls */}
          <div className="flex items-center gap-3">
            {!isRecording ? (
              <Button
                type="button"
                variant="outline"
                onClick={startRecording}
                disabled={audioUploading || saving}
                className="rounded-full flex items-center gap-2"
                data-testid="start-recording-btn"
              >
                <Mic className="w-4 h-4 text-red-500" />
                {locale === 'es-MX' ? 'Grabar' : 'Record'}
              </Button>
            ) : (
              <Button
                type="button"
                variant="destructive"
                onClick={stopRecording}
                className="rounded-full flex items-center gap-2 animate-pulse"
                data-testid="stop-recording-btn"
              >
                <Square className="w-4 h-4" />
                {formatTime(recordingTime)} / {formatTime(MAX_RECORDING_SECONDS)}
              </Button>
            )}
            
            {audioUploading && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">{locale === 'es-MX' ? 'Guardando...' : 'Saving...'}</span>
              </div>
            )}
          </div>
          
          {isNew && (
            <p className="text-xs text-muted-foreground">
              {locale === 'es-MX' 
                ? 'Al grabar, la idea se guardará automáticamente'
                : 'Save the idea first to record audio'}
            </p>
          )}
          
          {/* Audio Files List */}
          {(idea.audio_files || []).length > 0 && (
            <div className="space-y-2 pt-2">
              {idea.audio_files.map((audio, index) => (
                <Card key={audio.id} className="bg-secondary/30">
                  <CardContent className="p-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => playAudio(audio.id)}
                        className="rounded-full h-8 w-8"
                        data-testid={`play-audio-${index}`}
                      >
                        {playingAudioId === audio.id ? (
                          <Pause className="w-4 h-4 text-amber-500" />
                        ) : (
                          <Play className="w-4 h-4 text-amber-500" />
                        )}
                      </Button>
                      <div>
                        <p className="text-sm font-medium">
                          {locale === 'es-MX' ? 'Nota de voz' : 'Voice note'} #{index + 1}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {audio.created_at && format(parseISO(audio.created_at), 'dd MMM HH:mm', { locale: dateLocale })}
                          {audio.size_mb && ` • ${audio.size_mb}MB`}
                        </p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => deleteAudio(audio.id)}
                      className="rounded-full h-8 w-8 text-destructive hover:text-destructive"
                      data-testid={`delete-audio-${index}`}
                    >
                      <Trash className="w-4 h-4" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Bucket & Status */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>{t('ideas.bucket')}</Label>
              <QuickAddIdeaBucketButton onBucketCreated={fetchBuckets} />
            </div>
            <Select 
              value={idea.bucket_id || ''} 
              onValueChange={(value) => setIdea(prev => ({ ...prev, bucket_id: value }))}
            >
              <SelectTrigger data-testid="bucket-select">
                <SelectValue placeholder={locale === 'es-MX' ? 'Seleccionar cubeta' : 'Select bucket'} />
              </SelectTrigger>
              <SelectContent>
                {buckets.map(bucket => {
                  const BucketIcon = iconMap[bucket.icon] || Circle
                  return (
                    <SelectItem key={bucket.id} value={bucket.id}>
                      <div className="flex items-center gap-2">
                        <BucketIcon className="w-4 h-4" />
                        {bucket.name}
                      </div>
                    </SelectItem>
                  )
                })}
              </SelectContent>
            </Select>
          </div>

          {!isNew && (
            <div className="space-y-2">
              <Label>{t('tasks.status')}</Label>
              <Select 
                value={idea.status} 
                onValueChange={(value) => setIdea(prev => ({ ...prev, status: value }))}
              >
                <SelectTrigger data-testid="status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map(status => {
                    const StatusIcon = status.icon
                    return (
                      <SelectItem key={status.value} value={status.value}>
                        <div className="flex items-center gap-2">
                          <StatusIcon className="w-4 h-4" />
                          {t(`ideas.${status.label}`)}
                        </div>
                      </SelectItem>
                    )
                  })}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Tags */}
        <div className="space-y-2">
          <Label>{t('ideas.tags')}</Label>
          <div className="flex flex-wrap gap-2 mb-2">
            {idea.tags.map(tag => (
              <Badge 
                key={tag} 
                variant="secondary"
                className="flex items-center gap-1 pl-2 pr-1 py-1"
              >
                #{tag}
                <button
                  type="button"
                  onClick={() => removeTag(tag)}
                  className="ml-1 hover:bg-destructive/20 rounded-full p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            ))}
          </div>
          <div className="flex gap-2">
            <Input
              placeholder={t('ideas.addTag')}
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
              className="flex-1"
              data-testid="tag-input"
            />
            <Button type="button" variant="outline" onClick={addTag} size="icon">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Source URL */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Link className="w-4 h-4" />
            {t('ideas.sourceUrl')}
          </Label>
          <Input
            type="url"
            placeholder="https://..."
            value={idea.source_url || ''}
            onChange={(e) => setIdea(prev => ({ ...prev, source_url: e.target.value }))}
            data-testid="source-url-input"
          />
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col gap-3 pt-4">
          <Button
            onClick={handleSave}
            disabled={saving || !idea.body.trim()}
            className="w-full h-12 rounded-full bg-amber-500 hover:bg-amber-600"
            data-testid="save-idea-btn"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {t('ideas.save')}
          </Button>

          {!isNew && (
            <Button
              variant="outline"
              onClick={handleConvertToTask}
              className="w-full rounded-full"
              data-testid="convert-task-btn"
            >
              <CheckSquare className="w-4 h-4 mr-2 text-blue-500" />
              {t('ideas.convertToTask')}
            </Button>
          )}
        </div>

        {/* Metadata (for existing ideas) */}
        {!isNew && idea.created_at && (
          <div className="text-xs text-muted-foreground text-center pt-4 border-t">
            {locale === 'es-MX' ? 'Creada' : 'Created'}: {format(parseISO(idea.created_at), 'PPpp', { locale: dateLocale })}
            {idea.updated_at && idea.updated_at !== idea.created_at && (
              <> • {locale === 'es-MX' ? 'Actualizada' : 'Updated'}: {format(parseISO(idea.updated_at), 'PPpp', { locale: dateLocale })}</>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
